/**
 * ⭐ FAVORITES SERVICE
 * Admin API service for managing user favorites
 * Backend: https://polarride-api-17dr.vercel.app/api/admin/favorites
 */

import { getApiBaseUrl } from '@/config/environment';


// ========================================
// INTERFACES
// ========================================

export interface Favorite {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  entityType: 'vehicle' | 'driver';
  entityId: string;
  entityDetails: {
    id: string;
    name?: string;
    make?: string;
    model?: string;
    year?: number;
    licensePlate?: string;
    imageUrl?: string;
    rating?: number;
    totalRatings?: number;
  };
  createdAt: string;
}

export interface FavoritesFilters {
  page?: number;
  limit?: number;
  entityType?: 'vehicle' | 'driver';
  userId?: string;
  search?: string;
}

export interface FavoritesStats {
  overall: {
    totalFavorites: number;
    vehicleFavorites: number;
    driverFavorites: number;
    uniqueUsers: number;
    last7Days: number;
    last30Days: number;
    growthRate: number;
  };
  topVehicles: Array<{
    vehicleId: string;
    make: string;
    model: string;
    year: number;
    imageUrl?: string;
    favoriteCount: number;
    rating?: number;
  }>;
  topDrivers: Array<{
    driverId: string;
    name: string;
    imageUrl?: string;
    favoriteCount: number;
    rating?: number;
  }>;
  topUsers: Array<{
    userId: string;
    name: string;
    email: string;
    favoriteCount: number;
    vehicleFavorites: number;
    driverFavorites: number;
  }>;
  dailyTrend: Array<{
    date: string;
    totalFavorites: number;
    vehicleFavorites: number;
    driverFavorites: number;
  }>;
}

// ========================================
// SERVICE CLASS
// ========================================

class FavoritesService {
  private baseUrl = `${getApiBaseUrl()}/api/admin/favorites`;

  private getAuthHeaders() {
    return {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * 📋 Get all favorites with filters and pagination
   * GET /api/admin/favorites
   */
  async getFavorites(filters: FavoritesFilters = {}): Promise<{
    success: boolean;
    data?: {
      favorites: Favorite[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasMore: boolean;
      };
    };
    error?: string;
  }> {
    try {
      const params = new URLSearchParams();
      if (filters.page) params.append('page', String(filters.page));
      if (filters.limit) params.append('limit', String(filters.limit));
      if (filters.entityType) params.append('entityType', filters.entityType);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.search) params.append('search', filters.search);

      const url = `${this.baseUrl}${params.toString() ? `?${params}` : ''}`;

      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Favorites Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch favorites (${response.status})`);
      }

      const result = await response.json();

      // Transform backend response to match frontend interface
      if (result.success && result.data?.favorites) {
        result.data.favorites = result.data.favorites.map((fav: any) => {
          // Backend returns: user info (name, email, phone), entityType, entityId, createdAt
          // entity field is null - needs to be fetched separately

          const transformed = {
            ...fav,
            // Map user fields (backend may use 'name', 'email' directly or nested in 'user')
            userName: fav.userName || fav.name || fav.user?.name || fav.user?.fullName || 'Unknown User',
            userEmail: fav.userEmail || fav.email || fav.user?.email || 'N/A',
            userPhone: fav.userPhone || fav.phone || fav.user?.phone,
            // Ensure userId is set
            userId: fav.userId || fav.user?.id,
            // Entity fields
            entityType: fav.entityType,
            entityId: fav.entityId,
            // entityDetails might be null/empty since entity field is null
            entityDetails: fav.entityDetails || fav.entity || {
              id: fav.entityId,
              name: 'Entity no longer exists (deleted)'
            },
            createdAt: fav.createdAt
          };

          return transformed;
        });
      }

      return result;
    } catch (error) {
      console.error('❌ Error fetching favorites:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch favorites'
      };
    }
  }

  /**
   * 📊 Get comprehensive favorites statistics
   * GET /api/admin/favorites/stats
   */
  async getStats(): Promise<{
    success: boolean;
    data?: FavoritesStats;
    error?: string;
  }> {
    try {
      const url = `${this.baseUrl}/stats`;

      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Stats Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch statistics (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching statistics:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch statistics'
      };
    }
  }
}

export const favoritesService = new FavoritesService();
export default favoritesService;
